// The list with nothing selected (docs/SPEC.md item 4 "The list"; web/sheets.js renderNearby, ios NearbyList): what's
// on the map (this tab, filters, search) by time in the time tone's groups (RULES.nowThenSoonest): happening now
// nearest first, then soon, then later, each soonest first; while searching, every match; nothing in view → the
// soonest thing. As cards (the flyer above the row) or a compact list, switched by the toggle in its header and
// remembered per device. Desktop: in the side panel. Phone: a bar at the bottom (the header and the first two rows)
// that pulls up into the whole list. Rows are built PAGE at a time as the end scrolls into view.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FALLBACK_CENTER } from "../../data/config";
import type { FeedEvent } from "../../data/event";
import { distance, imageUrl } from "../../data/format";
import { SHARED, T, type CopyKey } from "../../shared/config";
import { Icon, type IconName } from "../../shared/icons";
import { observeResize } from "../../shared/resize";
import { RULES } from "../../shared/rules";
import { timed } from "../../perf";
import { useNow } from "../../state/clock";
import { useFeed } from "../../state/feed";
import { filtered, inViewTest, soonestFallback } from "../../state/filtered";
import { useProfileView } from "../../state/profile";
import { isSearching, moveMap, openSheet, setNearbyExpanded, useView, viewStore } from "../../state/view";
import { EmojiBadge } from "../../ui/EmojiBadge";
import { Flyer } from "../../ui/Flyer";
import { StarButton } from "../profile/slots";
import { TimeText } from "../../ui/TimeText";
import styles from "./NearbyList.module.css";

const PAGE = 30;
const VIEWS = SHARED.nearbyViews;
const savedView = () => {
  let v: string | null = null;
  try { v = localStorage.getItem(VIEWS.storageKey); } catch { /* private mode */ }
  return VIEWS.options.some((o) => o.id === v) ? v! : VIEWS.options[0]!.id;
};

interface Model { events: FeedEvent[]; title: string; sub?: string | null; fallback?: FeedEvent | null }

function useNearbyModel(): Model {
  const events = useFeed((s) => s.events), source = useFeed((s) => s.source);
  const filter = useView((s) => s.filter), camera = useView((s) => s.camera), loc = useView((s) => s.userLocation);
  const view = useProfileView(), now = useNow();
  const fs = filtered(), searching = isSearching(filter);
  const origin = loc || camera?.center || FALLBACK_CENTER;
  const groups = [...fs.primary, ...(searching ? fs.secondary : [])].map((e) => RULES.timeTone(e, now)[0]).join("");
  return useMemo(() => timed("list", () => {
    if (searching) {
      const all = [...fs.primary, ...fs.secondary];
      return { events: RULES.nowThenSoonest(all, origin, now),
        title: all.length ? (all.length === 1 ? T("list.oneResult") : T("list.manyResults", { n: all.length })) : T("list.noMatches"),
        sub: all.length ? null : T("list.noMatchesHint") };
    }
    if (source === "loading" && !events.length) return { events: [], title: T("list.loading") };
    const visible = fs.primary.filter(inViewTest());
    if (visible.length) return { events: RULES.nowThenSoonest(visible, origin, now), title: visible.length === 1 ? T("list.oneHere") : T("list.manyHere", { n: visible.length }) };
    const s = soonestFallback();
    return { events: s ? [s] : [], fallback: s, title: T("list.nothing", { phrase: RULES.tabEmpty(filter.time, now) }),
      sub: s ? T("list.soonest") : T("list.nothingHint") };
  // Remade only when what it shows can change: the filtered set, where the map is (or you are), loading, and an
    // event moving into another time group as the clock moves on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [fs.version, source, searching, origin.lat, origin.lng, searching ? null : camera?.bounds.join(), groups, view, filter.time]);
}

export function NearbyList() {
  const m = useNearbyModel(), expanded = useView((s) => s.nearbyExpanded), sheetOpen = useView((s) => !!s.sheet);
  const loc = useView((s) => s.userLocation);
  const [viewId, setViewId] = useState(savedView);
  const [shown, setShown] = useState(PAGE);
  const list = useRef<HTMLDivElement>(null), more = useRef<HTMLDivElement>(null), head = useRef<HTMLDivElement>(null);
  const [atTop, setAtTop] = useState(true);
  useEffect(() => setShown(PAGE), [m]);
  const rows = m.events.slice(0, shown);

  // The next page when the end of the list comes near.
  useEffect(() => {
    const el = more.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => { if (entries.some((x) => x.isIntersecting)) setShown((n) => n + PAGE); },
      { root: list.current, rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [rows.length, m.events.length]);

  // Phone: the bar is the header plus the first two rows; the map's buttons ride on top of it (--nearby-h). As cards
  // it's the same height (two list rows' worth), so switching views doesn't move the map.
  const rowH = useRef(59);
  useLayoutEffect(() => {
    const el = list.current;
    if (!el) return;
    const size = () => {
      const items = el.querySelectorAll<HTMLElement>("[data-mini]");
      let bar = head.current?.offsetHeight ?? 60;
      const asList = viewId === "list";
      if (asList && items[0]?.offsetHeight) rowH.current = items[0].offsetHeight;
      for (let i = 0; i < Math.min(2, items.length); i++) bar += asList ? items[i]!.offsetHeight : rowH.current;
      el.style.setProperty("--bar-h", `${bar + 8}px`);
      const h = sheetOpen ? 0 : el.getBoundingClientRect().height;
      document.body.style.setProperty("--nearby-h", `${viewStore.getState().nearbyExpanded ? 0 : Math.max(h, bar + 8)}px`);
    };
    size();
    return observeResize(el, size);
  }, [rows, viewId, expanded, sheetOpen]);

  // Phone: drag the header up to expand, down to shrink; a tap toggles.
  const startY = useRef<number | null>(null);
  useEffect(() => {
    const up = (ev: PointerEvent) => {
      if (startY.current == null) return;
      const dy = ev.clientY - startY.current;
      startY.current = null;
      const open = viewStore.getState().nearbyExpanded;
      if (dy < -25) setNearbyExpanded(true);
      else if (dy > 40) setNearbyExpanded(false);
      else if (Math.abs(dy) < 6 && (ev.target as Element).closest?.("#nearby-head")) setNearbyExpanded(!open);
    };
    addEventListener("pointerup", up);
    return () => removeEventListener("pointerup", up);
  }, []);
  useEffect(() => { if (list.current) list.current.scrollTop = 0; }, [expanded]);

  const pickView = (id: string) => {
    if (id === viewId) return;
    try { localStorage.setItem(VIEWS.storageKey, id); } catch { /* private mode */ }
    const items = list.current?.querySelector<HTMLElement>("[data-items]");
    if (!items?.animate || matchMedia("(prefers-reduced-motion: reduce)").matches) { setViewId(id); return; }
    // Switching crossfades.
    items.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 110, easing: "ease-in", fill: "forwards" }).finished.then(() => {
      setViewId(id);
      requestAnimationFrame(() => items.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: "ease-out" })
        .finished.then(() => items.getAnimations().forEach((a) => a.cancel()), () => {}));
    }, () => {});
  };

  const cls = [styles.nearby, viewId === "list" ? styles.asList : styles.asCards, expanded && styles.expanded, atTop && styles.atTop].filter(Boolean).join(" ");
  return (
    <div ref={list} id="nearby" className={cls} data-view={viewId} data-expanded={expanded || undefined} data-under-sheet={sheetOpen || undefined}
      onScroll={(ev) => setAtTop(ev.currentTarget.scrollTop <= 0)}>
      <div ref={head} id="nearby-head" className={styles.head}
        onPointerDown={(ev) => { if (!(ev.target as Element).closest("[data-view-toggle]")) startY.current = ev.clientY; }}>
        <button type="button" className={styles.grabber} aria-label="Show more or less" tabIndex={-1} />
        <div className={styles.titleRow}>
          <h3 id="nearby-title">{m.title}</h3>
          <div className={styles.toggle} id="nearby-view" role="radiogroup" data-view-toggle="">
            {VIEWS.options.map((o) => (
              <button key={o.id} type="button" id={`nearby-view-${o.id}`} role="radio" aria-checked={o.id === viewId}
                aria-label={T(o.label as CopyKey)} title={T(o.label as CopyKey)} className={styles.opt} onClick={() => pickView(o.id)}>
                <Icon name={o.icon as IconName} />
              </button>
            ))}
          </div>
        </div>
        {m.sub && <div className="muted small">{m.sub}</div>}
      </div>
      <div className={styles.items} data-items="">
        {rows.map((e) => <Mini key={e.id} event={e} fallback={e === m.fallback} from={loc} />)}
        {rows.length < m.events.length && <div ref={more} className={styles.more} aria-hidden="true" />}
      </div>
    </div>
  );
}

function Mini({ event: e, fallback, from }: { event: FeedEvent; fallback: boolean; from: { lat: number; lng: number } | null }) {
  const img = imageUrl(e);
  const meta = [distance(e, from), RULES.shortPrice(e)].filter(Boolean).map((x) => ` · ${x}`).join("");
  return (
    <div className={`${styles.mini}${img ? ` ${styles.hasImg}` : ""}`} data-mini="" data-event={e.id}>
      <button type="button" className={styles.open} data-id={e.id} onClick={() => {
        (document.activeElement as HTMLElement | null)?.blur?.();
        moveMap({ lat: e.lat, lng: e.lng }, fallback ? 14.5 : undefined);
        openSheet({ kind: "event", id: e.id });
      }}>
        {img && <Flyer src={img} className={styles.img} lazy />}
        <span className={styles.row}>
          <EmojiBadge event={e} />
          <span className={styles.text}>
            <span className={styles.t}>{e.title}</span>
            <span className={styles.m}><TimeText event={e} as="b" />{meta}</span>
          </span>
        </span>
      </button>
      <span className={styles.star}><StarButton event={e} place="mini" /></span>
    </div>
  );
}
